import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { 
  Check, X as XIcon, MessageCircle, ShoppingBag, Info, 
  ShieldCheck, MapPin, Star, Quote, ChevronRight, 
  User, Calendar, Clock, Heart, Share2, ArrowLeft, Sparkles, CheckCircle2
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api, { formatINR, formatErr, waLink } from "../lib/api";
import { resolveImageUrl } from "../components/ImageUpload";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { PackageCard } from "../components/PackageCard";

export default function PackageDetailPage() {
  const { slug } = useParams();
  const [pkg, setPkg] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [bookOpen, setBookOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", event_type: "", event_date: "", location: "", notes: "" });
  const [selectedLocation, setSelectedLocation] = useState("Room");
  const [similarPkgs, setSimilarPkgs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => { 
    window.scrollTo(0, 0);
    api.get(`/packages/${slug}`).then((r) => {
      setPkg(r.data);
      if (r.data.images?.length > 0) setActiveImg(0);
      
      // Fetch similar packages
      api.get("/packages").then(res => {
        const others = res.data.filter(p => p.category_slug === r.data.category_slug && p.slug !== r.data.slug);
        setSimilarPkgs(others.slice(0, 4));
      });
    }).catch(() => {});

    // Fetch real testimonials
    api.get("/testimonials").then(r => {
      setTestimonials(r.data.slice(0, 3));
    });
  }, [slug]);

  useEffect(() => { 
    if (pkg) document.title = `${pkg.title} — YoursDreamDecoration`; 
  }, [pkg]);

  if (!pkg) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-400 font-medium">Fetching details...</p>
      </div>
    </div>
  );

  const addonTotal = pkg.addons?.filter((a) => selectedAddons.includes(a.name)).reduce((s, a) => s + a.price, 0) || 0;
  const total = pkg.price + addonTotal;

  const toggleAddon = (name) => {
    setSelectedAddons((p) => p.includes(name) ? p.filter((n) => n !== name) : [...p, name]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/orders", { 
        ...form, 
        package_slug: pkg.slug, 
        addons: selectedAddons, 
        selected_location: selectedLocation 
      });
      toast.success("Booking received! Our team will contact you shortly on WhatsApp 🎉");
      setBookOpen(false);
      setForm({ name: "", phone: "", email: "", event_type: "", event_date: "", location: "", notes: "" });
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setSubmitting(false); }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: pkg.title,
        text: pkg.description,
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard!");
    }
  };

  const nextImg = () => setActiveImg((prev) => (prev + 1) % pkg.images.length);
  const prevImg = () => setActiveImg((prev) => (prev - 1 + pkg.images.length) % pkg.images.length);

  return (
    <div className="min-h-screen bg-slate-50/30 pb-24 md:pb-12" data-testid="package-detail">
      {/* Premium Header / Breadcrumb */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="p-2 hover:bg-slate-100 rounded-full transition-colors md:hidden">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </Link>
            <div className="hidden md:flex items-center gap-2 text-sm text-slate-400">
              <Link to="/" className="hover:text-primary transition">Home</Link>
              <span>/</span>
              <Link to={`/category/${pkg.category_slug}`} className="hover:text-primary transition capitalize">
                {pkg.category_slug?.replace("-", " ")}
              </Link>
              <span>/</span>
              <span className="text-slate-900 font-medium truncate max-w-[200px]">{pkg.title}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
             <Button variant="ghost" size="icon" onClick={handleShare} className="rounded-full">
               <Share2 className="w-4 h-4" />
             </Button>
             <Button variant="ghost" size="icon" className="rounded-full text-pink-500 hover:text-pink-600 hover:bg-pink-50">
               <Heart className="w-4 h-4" />
             </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          
          {/* LEFT CONTENT: Gallery & Details */}
          <div className="lg:col-span-8 space-y-10">
            <section className="space-y-4">
              <div className="relative group">
                <motion.div 
                  layoutId="main-img"
                  className="relative aspect-[16/10] sm:aspect-[16/9] rounded-[2.5rem] overflow-hidden bg-white shadow-2xl shadow-slate-200/50"
                >
                  <AnimatePresence mode="wait">
                    <motion.img 
                      key={activeImg}
                      initial={{ opacity: 0, scale: 1.05 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.5, ease: "circOut" }}
                      src={resolveImageUrl(pkg.images?.[activeImg])} 
                      alt={pkg.title} 
                      className="w-full h-full object-cover" 
                    />
                  </AnimatePresence>
                  
                  {/* Gallery Overlays */}
                  <div className="absolute top-6 left-6 flex gap-2">
                    <Badge className="bg-white/95 text-primary hover:bg-white border-none shadow-xl backdrop-blur-md px-4 py-2 text-[10px] font-black tracking-[0.2em] uppercase">
                      <Sparkles className="w-3 h-3 mr-2 inline" /> Premium Design
                    </Badge>
                  </div>

                  {/* Nav Arrows */}
                  {pkg.images?.length > 1 && (
                    <>
                      <button 
                        onClick={prevImg}
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-all hover:bg-white hover:text-slate-900"
                      >
                        <ArrowLeft className="w-6 h-6" />
                      </button>
                      <button 
                        onClick={nextImg}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-all hover:bg-white hover:text-slate-900"
                      >
                        <ChevronRight className="w-6 h-6" />
                      </button>
                    </>
                  )}
                </motion.div>
              </div>
              
              {pkg.images?.length > 1 && (
                <div className="flex gap-4 overflow-x-auto py-2 px-1 no-scrollbar">
                  {pkg.images.map((img, i) => (
                    <motion.button 
                      key={i} 
                      whileHover={{ y: -4 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setActiveImg(i)} 
                      className={`relative shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden border-2 transition-all duration-300 ${
                        i === activeImg ? "border-primary ring-4 ring-primary/10 shadow-lg" : "border-transparent opacity-60 hover:opacity-100"
                      }`} 
                    >
                      <img src={resolveImageUrl(img)} alt="" className="w-full h-full object-cover" />
                    </motion.button>
                  ))}
                </div>
              )}
            </section>

            {/* Description & Highlights */}
            <motion.section 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="space-y-6">
                <div className="flex flex-col gap-4">
                  <Badge className="w-fit bg-primary/10 text-primary hover:bg-primary/20 border-none px-4 py-1.5 text-[10px] font-black tracking-widest uppercase animate-pulse">
                    <Sparkles className="w-3.5 h-3.5 mr-2 inline" /> Exclusive Package
                  </Badge>
                  <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.1] tracking-tight">
                    {pkg.title}
                  </h1>
                  <div className="flex items-center gap-6 text-sm">
                    <div className="flex items-center gap-2 text-amber-500 font-bold bg-amber-50 px-3 py-1 rounded-full border border-amber-100">
                      <Star className="w-4 h-4 fill-current" />
                      <span>4.9 (120+ Reviews)</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-400">
                      <MapPin className="w-4 h-4" />
                      <span>Available in Delhi NCR</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-end gap-3 p-6 rounded-[2rem] bg-gradient-to-br from-primary/5 to-transparent border border-primary/10">
                  <div className="text-4xl sm:text-5xl font-bold text-primary">
                    {formatINR(pkg.price)}
                  </div>
                  {pkg.original_price && (
                    <div className="text-xl text-slate-400 line-through mb-1.5 decoration-slate-300">
                      {formatINR(pkg.original_price)}
                    </div>
                  )}
                  <Badge variant="outline" className="ml-2 mb-2 text-primary border-primary/20 bg-primary/5">
                    Save {Math.round(((pkg.original_price - pkg.price) / pkg.original_price) * 100)}%
                  </Badge>
                </div>
              </div>
              
              <p className="text-slate-600 text-lg leading-relaxed max-w-3xl">
                {pkg.description}
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { icon: Clock, label: "2-3 Hours", sub: "Setup Time" },
                  { icon: Calendar, label: "All Days", sub: "Availability" },
                  { icon: ShieldCheck, label: "Expert Team", sub: "Professional" },
                  { icon: ShoppingBag, label: "Fresh Items", sub: "Materials" }
                ].map((item, i) => (
                  <div key={i} className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm">
                    <item.icon className="w-5 h-5 text-primary mb-2" />
                    <div className="font-bold text-slate-900 text-sm leading-none">{item.label}</div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-widest mt-1 font-bold">{item.sub}</div>
                  </div>
                ))}
              </div>
            </motion.section>

            <Separator className="bg-slate-100" />
            <div className="grid md:grid-cols-2 gap-10">
              <motion.div 
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: "circOut" }}
                className="space-y-8"
              >
                <div className="space-y-2">
                  <h3 className="flex items-center gap-3 font-serif-display text-3xl text-slate-900">
                    <div className="w-10 h-10 rounded-2xl bg-green-50 flex items-center justify-center shadow-sm">
                      <Check className="w-5 h-5 text-green-600 stroke-[3]" />
                    </div>
                    Inclusions
                  </h3>
                  <p className="text-sm text-slate-400 font-medium ml-13">Everything you get in this package</p>
                </div>
                <ul className="space-y-4">
                  {pkg.inclusions?.map((it, i) => (
                    <motion.li 
                      key={i} 
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="flex gap-4 text-slate-600 leading-relaxed group p-4 rounded-2xl hover:bg-white hover:shadow-xl hover:shadow-slate-100 transition-all duration-300"
                    >
                      <div className="mt-1.5 w-2 h-2 rounded-full bg-green-400 shrink-0 group-hover:scale-150 transition-transform" />
                      <span className="font-medium">{it}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: "circOut" }}
                className="space-y-8"
              >
                <div className="space-y-2">
                  <h3 className="flex items-center gap-3 font-serif-display text-3xl text-slate-900 opacity-60">
                    <div className="w-10 h-10 rounded-2xl bg-slate-50 flex items-center justify-center shadow-sm">
                      <Info className="w-5 h-5 text-slate-400 stroke-[3]" />
                    </div>
                    Exclusions
                  </h3>
                  <p className="text-sm text-slate-400 font-medium ml-13">Important things to note</p>
                </div>
                <ul className="space-y-4">
                  {pkg.exclusions?.map((it, i) => (
                    <motion.li 
                      key={i} 
                      initial={{ opacity: 0, x: 10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="flex gap-4 text-slate-400 text-sm leading-relaxed italic p-4 rounded-2xl hover:bg-white hover:shadow-xl hover:shadow-slate-100 transition-all duration-300"
                    >
                      <XIcon className="w-4 h-4 shrink-0 mt-1 opacity-50" />
                      <span>{it}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* How it Works Section */}
            <section className="bg-slate-900 text-white p-10 rounded-[3rem] relative overflow-hidden shadow-2xl shadow-slate-200">
               <div className="relative z-10 space-y-8">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="font-serif-display text-3xl">How to book this surprise?</h3>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-8">
                    {[
                      { step: "01", title: "Select Add-ons", desc: "Choose extra balloons, cakes, or gifts." },
                      { step: "02", title: "Fill Details", desc: "Share your event date and venue details." },
                      { step: "03", title: "Relax & Enjoy", desc: "Our team arrives to make it magical." }
                    ].map((item, i) => (
                      <div key={i} className="space-y-3 group">
                        <div className="text-primary font-black text-xs tracking-[0.2em] opacity-50 group-hover:opacity-100 transition-opacity">{item.step}</div>
                        <div className="font-bold text-xl">{item.title}</div>
                        <div className="text-slate-400 text-sm leading-relaxed">{item.desc}</div>
                      </div>
                    ))}
                  </div>
               </div>
               <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-[120px] -mr-48 -mt-48" />
            </section>

            {/* Why Choose Us */}
            <section className="grid sm:grid-cols-2 gap-6">
              {[
                { title: "On-Time Setup", desc: "Our team arrives 2 hours early to ensure everything is perfect.", icon: Clock, color: "bg-blue-50 text-blue-600" },
                { title: "Premium Quality", desc: "We use only the highest quality balloons and materials.", icon: ShieldCheck, color: "bg-green-50 text-green-600" },
                { title: "Custom Designs", desc: "Every decoration is tailored to your specific preferences.", icon: Sparkles, color: "bg-purple-50 text-purple-600" },
                { title: "Hassle-Free", desc: "Just book and relax. We handle everything from setup to cleanup.", icon: Heart, color: "bg-pink-50 text-pink-600" }
              ].map((item, i) => (
                <div key={i} className="flex gap-4 p-8 bg-white rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-xl hover:border-primary/10 transition-all duration-300">
                  <div className={`w-14 h-14 rounded-2xl ${item.color} flex items-center justify-center shrink-0 shadow-sm`}>
                    <item.icon className="w-7 h-7" />
                  </div>
                  <div className="space-y-1">
                    <h4 className="font-bold text-slate-900 text-lg">{item.title}</h4>
                    <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </section>

            {/* Customization / Add-ons Overhaul */}
            {pkg.addons?.length > 0 && (
              <motion.section 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="space-y-8"
              >
                <div className="flex items-end justify-between">
                  <h3 className="font-serif-display text-3xl text-slate-900">Elevate your celebration</h3>
                  <Badge variant="secondary" className="bg-primary/5 text-primary border-none">OPTIONAL</Badge>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {pkg.addons.map((a, i) => (
                    <motion.div
                      key={i}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => toggleAddon(a.name)}
                      className={`group cursor-pointer p-5 rounded-[2rem] border-2 transition-all duration-500 relative overflow-hidden ${
                        selectedAddons.includes(a.name) 
                          ? "border-primary bg-primary/[0.03] shadow-xl shadow-primary/5" 
                          : "border-slate-100 bg-white hover:border-slate-200"
                      }`}
                    >
                      <div className="flex items-center justify-between relative z-10">
                        <div className="flex items-center gap-4">
                          <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${
                            selectedAddons.includes(a.name) ? "bg-primary border-primary scale-110" : "border-slate-200"
                          }`}>
                            {selectedAddons.includes(a.name) && <Check className="w-4 h-4 text-white" />}
                          </div>
                          <div>
                            <div className="font-bold text-slate-800">{a.name}</div>
                            <div className="text-xs text-slate-400 font-medium">Add to package</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-primary font-black">+ {formatINR(a.price)}</div>
                        </div>
                      </div>
                      {selectedAddons.includes(a.name) && (
                        <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full -mr-12 -mt-12 blur-2xl" />
                      )}
                    </motion.div>
                  ))}
                </div>
              </motion.section>
            )}
          </div>

          {/* RIGHT SIDEBAR: Sticky Booking Card */}
          <div className="lg:col-span-4">
            <div className="sticky top-24 space-y-6">
              <Card className="rounded-[2.5rem] overflow-hidden border-white/20 shadow-2xl shadow-slate-200/50 bg-white/60 backdrop-blur-3xl">
                <CardContent className="p-8 space-y-8">
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-primary" />
                      Plan Your Celebration
                    </h3>
                    <div className="p-5 rounded-2xl bg-primary/5 border border-primary/10 space-y-2">
                      <div className="flex justify-between text-sm text-slate-600">
                        <span>Base Package</span>
                        <span className="font-bold">{formatINR(pkg.price)}</span>
                      </div>
                      {selectedAddons.length > 0 && (
                        <div className="flex justify-between text-sm text-slate-600">
                          <span>Addons ({selectedAddons.length})</span>
                          <span className="font-bold">{formatINR(addonTotal)}</span>
                        </div>
                      )}
                      <Separator className="my-3 bg-primary/10" />
                      <div className="flex justify-between text-lg text-slate-900 font-black">
                        <span>Total Estimate</span>
                        <span className="text-primary">{formatINR(total)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Location Toggle */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                       <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Event Location</label>
                       <span className="text-xs font-bold text-primary">{selectedLocation}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {["Room", "Open", "Hotel"].map((loc) => (
                        <button
                          key={loc}
                          onClick={() => setSelectedLocation(loc)}
                          className={`py-3 rounded-xl text-[11px] font-black tracking-widest uppercase border-2 transition-all ${
                            selectedLocation === loc
                              ? "border-primary bg-primary/5 text-primary shadow-lg shadow-primary/10"
                              : "border-transparent bg-slate-50 hover:bg-slate-100 text-slate-400"
                          }`}
                        >
                          {loc}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <Dialog open={bookOpen} onOpenChange={setBookOpen}>
                      <DialogTrigger asChild>
                        <Button className="w-full h-16 rounded-2xl bg-primary hover:bg-primary/90 text-lg font-black gap-3 shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all border-none">
                          <ShoppingBag className="w-6 h-6" /> Book Now
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md p-0 overflow-hidden rounded-[2.5rem] border-none shadow-3xl bg-white">
                        <div className="bg-primary p-8 text-white relative">
                          <div className="relative z-10">
                            <DialogTitle className="font-serif-display text-4xl mb-2 leading-tight">Secure your <br/> Celebration</DialogTitle>
                            <p className="text-white/70 font-medium">We'll contact you shortly to confirm.</p>
                          </div>
                          <Sparkles className="absolute top-8 right-8 w-12 h-12 text-white/10" />
                        </div>
                        <div className="p-8">
                          <form onSubmit={submit} className="space-y-5">
                            <div className="space-y-2">
                              <Label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Full Name</Label>
                              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl h-12 border-slate-100 bg-slate-50/50 focus:ring-primary/20" placeholder="e.g. Rahul Kumar" />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">WhatsApp Number</Label>
                              <Input type="tel" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl h-12 border-slate-100 bg-slate-50/50 focus:ring-primary/20" placeholder="+91 ..." />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Event Type</Label>
                                <Input required value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} className="rounded-xl h-12 border-slate-100 bg-slate-50/50 focus:ring-primary/20" placeholder="Birthday" />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Event Date</Label>
                                <Input type="date" required value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} className="rounded-xl h-12 border-slate-100 bg-slate-50/50 focus:ring-primary/20" />
                              </div>
                            </div>
                            <Button type="submit" disabled={submitting} className="w-full h-16 rounded-2xl bg-primary text-lg font-black shadow-lg shadow-primary/20 border-none mt-4">
                              {submitting ? "Processing..." : "Confirm Booking"}
                            </Button>
                          </form>
                        </div>
                      </DialogContent>
                    </Dialog>
                    
                    <div className="pt-4 flex items-center gap-3 text-slate-500">
                      <Star className="w-5 h-5 text-amber-500" />
                      <span className="text-sm font-medium">Trusted by 500+ Happy Clients</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Promo Card */}
              <div className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-[2.5rem] p-8 text-white relative overflow-hidden shadow-xl shadow-indigo-100">
                <div className="relative z-10 space-y-4">
                  <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center mb-4">
                    <Heart className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="font-serif-display text-2xl leading-tight">Need a customized <br/> surprise plan?</h4>
                  <p className="text-white/70 text-sm leading-relaxed font-medium">Our expert planners can create a unique theme just for you.</p>
                  <Button variant="secondary" className="w-full rounded-xl bg-white text-indigo-600 hover:bg-slate-100 font-bold border-none h-12">
                    Request Custom Theme
                  </Button>
                </div>
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
              </div>
              {/* Trust Section */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white/50 backdrop-blur-xl rounded-[2.5rem] border border-white p-8 shadow-xl shadow-slate-100/50 space-y-8"
            >
              <div className="text-center space-y-2">
                <h4 className="font-serif-display text-2xl text-slate-900">Premium Experience</h4>
                <p className="text-sm text-slate-500 font-medium italic">We ensure every moment is magical</p>
              </div>
              
              <div className="grid gap-6">
                <div className="flex items-center gap-5 p-4 rounded-3xl bg-white/40 border border-white shadow-sm hover:shadow-md transition-all group">
                  <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <CheckCircle2 className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h5 className="text-sm font-bold text-slate-800">Quality Guaranteed</h5>
                    <p className="text-[12px] text-slate-500 leading-tight">Professional grade decorations with premium quality balloons & props.</p>
                  </div>
                </div>

                <div className="flex items-center gap-5 p-4 rounded-3xl bg-white/40 border border-white shadow-sm hover:shadow-md transition-all group">
                  <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <ShieldCheck className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h5 className="text-sm font-bold text-slate-800">Verified Decorators</h5>
                    <p className="text-[12px] text-slate-500 leading-tight">All our decorators are background checked & highly experienced.</p>
                  </div>
                </div>

                <div className="flex items-center gap-5 p-4 rounded-3xl bg-white/40 border border-white shadow-sm hover:shadow-md transition-all group">
                  <div className="w-12 h-12 rounded-2xl bg-green-50 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Clock className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h5 className="text-sm font-bold text-slate-800">On-Time Execution</h5>
                    <p className="text-[12px] text-slate-500 leading-tight">We value your time. Setup completed strictly as per schedule.</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>

      {/* REVIEWS SECTION - REAL STORIES */}
      <section className="bg-white py-24 border-y border-slate-100 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <div className="space-y-4">
              <Badge className="bg-primary/10 text-primary border-none px-4 py-1 text-[10px] font-black tracking-widest uppercase">
                Happy Customers
              </Badge>
              <h2 className="font-serif-display text-5xl lg:text-6xl text-slate-900 leading-[1.1]">Real Stories from <br/> our community</h2>
            </div>
            <div className="flex items-center gap-6 bg-slate-50 p-6 rounded-[2rem] border border-slate-100 shadow-sm">
               <div className="text-center px-4">
                 <div className="text-4xl font-serif-display font-black text-slate-900 leading-none">4.9</div>
                 <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black mt-2">Rating</div>
               </div>
               <Separator orientation="vertical" className="h-10 bg-slate-200" />
               <div className="px-4 text-center">
                 <div className="text-4xl font-serif-display font-black text-slate-900 leading-none">500+</div>
                 <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black mt-2">Smiles</div>
               </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {(testimonials && testimonials.length > 0 ? testimonials : [
              { name: "Ananya Sharma", city: "Delhi", text: "Truly a professional team. The setup was ready before time and the quality was premium. Highly recommended for anniversaries!", rating: 5 },
              { name: "Varun Malhotra", city: "Gurgaon", text: "Amazing candle light dinner setup. My partner loved it! Very punctual and cooperative team. The balloons were of great quality.", rating: 5 },
              { name: "Ishani Gupta", city: "Noida", text: "Decorated my terrace for a birthday party. It looked magical under the fairy lights. The team was very creative.", rating: 5 }
            ]).map((rev, i) => (
              <motion.div 
                key={i} 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
              >
                <Card className="border-none shadow-xl shadow-slate-200/40 bg-slate-50/30 hover:bg-white hover:-translate-y-2 transition-all duration-500 rounded-[2.5rem] overflow-hidden group h-full">
                  <CardContent className="p-8 sm:p-10 flex flex-col h-full justify-between space-y-8">
                    <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <div className="flex gap-1 text-amber-400">
                          {[...Array(5)].map((_, j) => (
                            <Star key={j} className={`w-4 h-4 ${j < (rev.rating || 5) ? "fill-current" : "text-slate-200"}`} />
                          ))}
                        </div>
                        <div className="p-2 bg-green-50 rounded-full">
                          <ShieldCheck className="w-4 h-4 text-green-500" />
                        </div>
                      </div>
                      
                      <div className="relative">
                        <Quote className="absolute -top-4 -left-4 w-8 h-8 text-slate-200 group-hover:text-primary/20 transition-colors -z-10" />
                        <p className="text-slate-700 text-lg leading-relaxed italic font-medium">"{rev.text}"</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 pt-6 border-t border-slate-100/80">
                      <div className="w-12 h-12 rounded-full bg-slate-200 overflow-hidden shadow-inner flex-shrink-0 border-2 border-white">
                        <img 
                          src={rev.image || `https://api.dicebear.com/7.x/avataaars/svg?seed=${rev.name}`} 
                          alt={rev.name} 
                          className="w-full h-full object-cover" 
                          onError={(e) => { e.target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${rev.name}`; }}
                        />
                      </div>
                      <div>
                        <div className="font-black text-slate-900 text-sm uppercase tracking-wider">{rev.name}</div>
                        <div className="text-xs text-slate-400 font-bold">{rev.city || "Verified Client"}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US - VALUE PROPOSITION */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <Badge className="bg-primary/10 text-primary border-none px-4 py-1 text-[10px] font-black tracking-widest uppercase">Our Commitment</Badge>
                <h2 className="text-4xl md:text-5xl font-serif-display text-slate-900 leading-tight">Why we are trusted by <br/> over 500+ families</h2>
                <p className="text-slate-500 text-lg leading-relaxed">We don't just decorate rooms; we create memories that last a lifetime. Our commitment to quality and punctuality is what sets us apart.</p>
              </div>

              <div className="grid gap-6">
                {[
                  { icon: ShieldCheck, title: "Premium Quality Guarantee", desc: "We use only the finest materials and branded balloons for every setup." },
                  { icon: Clock, title: "On-Time Setup", desc: "Our team arrives exactly as per your scheduled slot, no delays." },
                  { icon: User, title: "Expert Stylists", desc: "Each setup is handled by trained decorators with years of experience." }
                ].map((item, i) => (
                  <div key={i} className="flex gap-6 group">
                    <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center shrink-0 group-hover:bg-primary/10 transition-colors">
                      <item.icon className="w-6 h-6 text-slate-400 group-hover:text-primary transition-colors" />
                    </div>
                    <div className="space-y-1">
                      <h4 className="text-lg font-black text-slate-900 uppercase tracking-tight">{item.title}</h4>
                      <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-tr from-primary/10 to-transparent rounded-[3rem] -z-10 blur-2xl" />
              <div className="bg-slate-50 rounded-[3rem] p-12 space-y-8 border border-slate-100">
                <div className="space-y-2">
                  <h3 className="text-2xl font-serif-display text-slate-900">Your Happiness, Guaranteed.</h3>
                  <p className="text-slate-500">If you are not 100% satisfied with our service, we'll make it right.</p>
                </div>
                <div className="flex -space-x-3">
                  {[1,2,3,4,5].map(i => (
                    <img key={i} src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${i+10}`} className="w-12 h-12 rounded-full border-4 border-white shadow-sm" alt="user" />
                  ))}
                  <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-white text-xs font-black border-4 border-white shadow-sm">+50</div>
                </div>
                <div className="pt-4">
                  <Button variant="outline" className="rounded-2xl border-2 px-8 h-12 uppercase text-[10px] tracking-widest font-black">Learn about our values</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS SECTION */}
      <section className="py-24 bg-slate-900 text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-primary/5 blur-[120px] -z-0" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-20 space-y-4">
            <Badge className="bg-white/10 text-white border-none px-4 py-1 text-[10px] font-black tracking-widest uppercase">The Experience</Badge>
            <h2 className="text-5xl md:text-6xl font-serif-display leading-tight">Crafting your perfect <br/> celebration in 4 steps</h2>
          </div>

          <div className="grid md:grid-cols-4 gap-4 relative">
            {/* Connecting Line - Desktop Only */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-px bg-white/10 -translate-y-1/2 -z-0" />
            
            {[
              { step: "01", title: "Select & Customise", desc: "Pick your favourite setup and add your personal touches." },
              { step: "02", title: "Instant Booking", desc: "Fill in the details and secure your slot instantly." },
              { step: "03", title: "Expert Setup", desc: "Our professional team arrives and creates the magic." },
              { step: "04", title: "Time to Celebrate", desc: "Walk in to your dream setup and enjoy the moment." }
            ].map((s, i) => (
              <div key={i} className="relative group p-10 rounded-[3rem] bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-500">
                <div className="relative z-10 space-y-6">
                   <div className="w-16 h-16 rounded-[2rem] bg-primary flex items-center justify-center text-2xl font-black shadow-2xl shadow-primary/40 transition-transform group-hover:scale-110 duration-500">
                     {s.step}
                   </div>
                   <div className="space-y-3">
                     <h3 className="text-2xl font-serif-display tracking-tight">{s.title}</h3>
                     <p className="text-slate-400 text-sm leading-relaxed">{s.desc}</p>
                   </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-white border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
           <div className="text-center mb-16 space-y-4">
             <Badge className="bg-primary/10 text-primary border-none px-4 py-1 text-[10px] font-black tracking-widest uppercase">Support</Badge>
             <h2 className="text-4xl md:text-5xl font-serif-display text-slate-900">Common Questions</h2>
             <p className="text-slate-500 text-lg">Everything you need to know about our premium service.</p>
           </div>

           <div className="grid gap-4">
             {[
               { q: "Is the decoration same as shown in photos?", a: "Yes, we strive to make it 95-100% same. We use the exact same materials and patterns as shown in the portfolio images." },
               { q: "Do you provide same day service?", a: "Yes! We accept same-day bookings for most locations if booked before 2 PM. However, we recommend 24-48 hours for complex setups." },
               { q: "How much time does setup take?", a: "Our standard room decorations take about 90-120 minutes. Larger event setups might take 3-4 hours." },
               { q: "Can I customize the colors?", a: "Absolutely! You can choose your preferred balloon colors and themes. Just mention it in the booking notes or tell our team on WhatsApp." },
               { q: "What about cleanup after the event?", a: "Our team focuses on setup. Cleanup is typically handled by the venue or client, but we use high-quality materials that are easy to remove without damaging walls." },
               { q: "How much time before should I book?", a: "We recommend booking at least 2-3 days in advance to secure your preferred slot, especially on weekends." }
             ].map((item, i) => (
               <FAQItem key={i} question={item.q} answer={item.a} />
             ))}
           </div>
        </div>
      </section>

      {/* SIMILAR DECORATIONS SECTION */}
      {similarPkgs.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="flex items-center justify-between mb-12">
            <div>
              <Badge variant="outline" className="text-secondary border-secondary/20 bg-secondary/5 mb-4 px-3 py-1 text-[10px] font-black tracking-widest uppercase">
                Explore More
              </Badge>
              <h2 className="font-serif-display text-4xl lg:text-5xl text-slate-900">Similar Celebrations</h2>
            </div>
            <Link to={`/category/${pkg.category_slug}`} className="group flex items-center gap-2 text-primary font-black uppercase text-[10px] tracking-widest">
              View All <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {similarPkgs.map((p) => (
              <PackageCard key={p.slug} pkg={p} />
            ))}
          </div>
        </section>
      )}

      {/* STICKY BOTTOM BAR - Mobile Only */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/90 backdrop-blur-xl border-t border-slate-100 md:hidden z-50 flex items-center justify-between gap-4 shadow-[0_-10px_30px_rgba(0,0,0,0.08)]">
        <div className="pl-2">
          <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-black block mb-0.5">Final Total</span>
          <span className="text-2xl font-black text-primary leading-none tracking-tight">{formatINR(total)}</span>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setBookOpen(true)} className="rounded-2xl bg-primary px-8 h-14 font-black shadow-xl shadow-primary/20 text-md uppercase tracking-wider">
            Book Now
          </Button>
          <a href={waLink(`Hi, I want to book "${pkg.title}"`)} target="_blank" rel="noreferrer">
            <Button variant="outline" className="rounded-2xl border-2 border-primary/20 w-14 h-14 p-0 flex items-center justify-center text-primary bg-primary/5">
              <MessageCircle className="w-6 h-6" />
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
}

function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="bg-white rounded-[2rem] border border-slate-100 overflow-hidden transition-all duration-300 hover:shadow-lg hover:border-primary/20">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-6 text-left flex items-center justify-between gap-4 group"
      >
        <span className="font-bold text-slate-900 text-lg group-hover:text-primary transition-colors">{question}</span>
        <div className={`w-8 h-8 rounded-full border border-slate-100 flex items-center justify-center transition-all duration-300 ${isOpen ? "bg-primary border-primary rotate-180" : ""}`}>
          <ChevronRight className={`w-4 h-4 transition-colors ${isOpen ? "text-white" : "text-slate-400"}`} />
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="px-6 pb-6 text-slate-500 leading-relaxed"
          >
            {answer}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

